import { useEffect, useState, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { intl } from '@trello/i18n';
import { pauseShortcuts, resumeShortcuts } from '@trello/keybindings';
import { Spotlight } from '@trello/nachos/onboarding-experimental';
import { useSplitScreenSharedState } from '@trello/split-screen';
import { token } from '@trello/theme';

import plannerDiscoverySpotlightImage from './planner-discovery-spotlight.gif';
import {
  PLANNER_DISCOVERY_SPOTLIGHT_MESSAGE_ID,
  PLANNER_DISCOVERY_SPOTLIGHT_TARGET,
} from './PlannerDiscoverySpotlight.constants';
import { useIsEligibleForPlannerDiscoverySpotlight } from './useIsEligibleForPlannerDiscoverySpotlight';

export const PlannerDiscoverySpotlight: FunctionComponent = () => {
  const { isEligible } = useIsEligibleForPlannerDiscoverySpotlight();
  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();
  const { togglePlanner } = useSplitScreenSharedState();
  const [isSpotlightActive, setIsSpotlightActive] = useState(false);

  const onNextFrame = (func: () => void, delay = 0): number => {
    const nextFrameTimeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(func));
    }, delay);
    return nextFrameTimeoutId;
  };

  useEffect(() => {
    if (isEligible) {
      const nextFrameTimeoutId = onNextFrame(() => {
        setIsSpotlightActive(true);
        Analytics.sendViewedComponentEvent({
          componentType: 'spotlight',
          componentName: 'plannerDiscoverySpotlight',
          source: 'plannerDiscoverySpotlight',
        });
      }, 250);
      return () => window.clearTimeout(nextFrameTimeoutId);
    }
  }, [isEligible]);

  useEffect(() => {
    if (isSpotlightActive) {
      pauseShortcuts();
    } else if (!isSpotlightActive) {
      resumeShortcuts();
    }
  }, [isSpotlightActive]);

  const dismiss = () => {
    dismissOneTimeMessage(PLANNER_DISCOVERY_SPOTLIGHT_MESSAGE_ID);
  };
  const handleDismiss = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'plannerDiscoverySpotlightDismissButton',
      source: 'plannerDiscoverySpotlight',
    });
    dismiss();
  };

  const handleOpen = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'plannerDiscoverySpotlightOpenButton',
      source: 'plannerDiscoverySpotlight',
    });
    togglePlanner();
    dismiss();
  };

  if (!isEligible || !isSpotlightActive) {
    return null;
  }

  return (
    <Spotlight
      key={PLANNER_DISCOVERY_SPOTLIGHT_TARGET}
      heading={intl.formatMessage({
        id: 'templates.new_user_discovery.spotlight-planner-title',
        defaultMessage: 'Protect your time with Trello Planner',
        description: 'Heading for planner discovery spotlight',
      })}
      target={PLANNER_DISCOVERY_SPOTLIGHT_TARGET}
      actions={[
        {
          text: intl.formatMessage({
            id: 'templates.new_user_discovery.spotlight-planner-open',
            defaultMessage: 'Open',
            description: 'Open button for planner discovery spotlight',
          }),
          onClick: handleOpen,
          appearance: 'primary',
        },
        {
          text: intl.formatMessage({
            id: 'templates.new_user_discovery.spotlight-planner-dismiss',
            defaultMessage: 'Dismiss',
            description: 'Dismiss button for planner discovery spotlight',
          }),
          onClick: handleDismiss,
          appearance: 'subtle',
        },
      ]}
      image={plannerDiscoverySpotlightImage}
      dialogPlacement="top center"
      dialogWidth={275}
      targetRadius={3}
      targetBgColor={token('elevation.surface.overlay', '#FFFFFF')}
    >
      <FormattedMessage
        id="templates.new_user_discovery.spotlight-planner-description"
        defaultMessage="Connect your calendar, schedule your cards, and create focus time to get things done."
        description="Description for planner discovery spotlight"
      />
    </Spotlight>
  );
};
